<template>
  <section class="customer-search" @touchmove="handleTouch" @click="handleTouch">
    <div class="left">
      <div class="title">
        <span v-if="type === 'EARN'">{{ t('title.point_earning') }}</span>
        <span v-else>{{ t('title.customer_search') }}</span>
      </div>
      <i18n-t keypath="info.input_mobile_number" tag="div" class="description">
        <template #mobile_number>
          <span>{{ t('label.mobile_number') }}</span>
        </template>
      </i18n-t>
      <div class="phone-image"><img src="@/assets/images/img-phone.png" alt="휴대전화 이미지" /></div>
    </div>
    <div class="right">
      <BaseKeypadPhone v-model="mobileNumber" />
      <div class="button">
        <BaseButtonGroup>
          <BaseButton
            class="outline"
            :count="autoCloseTime"
            :flex="1"
            @mouseup="handlePrevClick"
            @touchend.prevent="handlePrevClick"
          >
            <template v-if="type !== 'EARN'" #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
                <path
                  id="arrow-left-solid"
                  d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                  transform="translate(-0.025 -63.925)"
                />
              </svg>
            </template>
            <template v-if="type === 'EARN'">{{ t('button.cancel') }}</template>
            <template v-else>{{ t('button.prev') }}</template>
          </BaseButton>
          <BaseButton :flex="2" @mouseup="handleConfirmClick" @touchend.prevent="handleConfirmClick">
            <template #icon>
              <img src="@/assets/images/icon-check-white.svg" alt="확인 아이콘" />
            </template>
            {{ t('button.confirm') }}
          </BaseButton>
        </BaseButtonGroup>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { getCustomer } from '@/api/server';
  import UserError from '@/error';
  import OrderCloudCustomer from '@/interfaces/OrderCloudCustomer';
  import { useCommonStore } from '@/stores/common';
  import { useModalStore } from '@/stores/modal';
  import { AxiosError } from 'axios';
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import BaseButton from './base/BaseButton.vue';
  import BaseButtonGroup from './base/BaseButtonGroup.vue';
  import BaseKeypadPhone from './base/BaseKeypadPhone.vue';
  import CustomerRegister from './CustomerRegister.vue';

  const props = defineProps<{
    /** 회원 가입 프로세스 진행 여부 */
    type: 'USE' | 'EARN';
  }>();

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const modalStore = useModalStore();

  let timer: any;
  const time = 30;

  const mobileNumber = ref('010');

  // 자동 모달 닫는 시간 (초)
  const autoCloseTime = ref(0);

  // 8자리 입력시 자동 확인
  watch(mobileNumber, () => {
    // if (newVal.length === 8) {
    //   handleConfirmClick();
    // }
    handleTouch();
  });

  onMounted(() => {
    if (props.type === 'EARN') {
      commonStore.clearWaitingPopupInterval();

      autoCloseTime.value = time;
      timer = setInterval(() => {
        if (--autoCloseTime.value === 0) {
          handlePrevClick();
        }
      }, 1000);
    }
  });

  onBeforeUnmount(() => {
    if (props.type === 'EARN') {
      clearInterval(timer);
      commonStore.setWaitingPopupInterval();
    }
  });

  const handleTouch = () => {
    if (props.type === 'EARN') {
      autoCloseTime.value = time;
    }
  };

  /** 이전 버튼 클릭 */
  const handlePrevClick = () => modalStore.resolveSyncModal<OrderCloudCustomer | null>(null);

  /** 확인 버튼 클릭 */
  const handleConfirmClick = async () => {
    if (mobileNumber.value.length < 11) throw new UserError(t('error.not_valid_mobile_number'));

    const loadingId = commonStore.showLoading();
    try {
      // 고객 조회
      const customer = await getCustomer(mobileNumber.value);

      // 조회된 고객이 있는 경우 반환 후 종료
      if (customer) {
        modalStore.resolveSyncModal<OrderCloudCustomer | null>(customer);
        return;
      }

      // 조회된 고객이 없는 경우 회원 가입 진행 여부 검사 (회원 포인트 적립 시에만)
      if (props.type === 'EARN') {
        const isOk = await commonStore.showDialog({
          type: 'confirm',
          message: t('info.ask_register'),
        });

        // 회원 가입 안함 선택시 null 반환
        if (!isOk) {
          modalStore.resolveSyncModal<OrderCloudCustomer | null>(null);
          return;
        }

        // 자동 취소 타이머 해제
        clearInterval(timer);

        // 회원 가입 모달을 열어 가입 진행 후 회원 정보 받기
        const registeredCustomer = await modalStore.openSyncModalAsync<
          typeof CustomerRegister,
          OrderCloudCustomer | null
        >(CustomerRegister, {
          mobileNumber: mobileNumber.value,
        });

        // 회원 가입 성공 여부 검사
        if (registeredCustomer) {
          // 가입된 회원 정보 반환
          modalStore.resolveSyncModal<OrderCloudCustomer | null>(registeredCustomer);
          return;
        } else {
          // 회원 가입 취소시 null 반환
          modalStore.resolveSyncModal<OrderCloudCustomer | null>(null);
          return;
        }
      } else {
        await commonStore.showDialog({
          type: 'alert',
          message: t('error.not_found_customer'),
        });
        return;
      }
    } catch (error: any) {
      if (error instanceof AxiosError && error.response && error.response.status === 409) {
        throw new UserError(t('error.duplicate_customer'));
      } else {
        throw error;
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .customer-search {
    display: grid;
    grid-template-columns: 42.9688vw 1fr;
    width: 100%;
    height: 100%;
    @media (max-width: vars.$sm) {
      grid-template-columns: auto;
      grid-template-rows: auto 1fr;
    }

    .left {
      display: grid;
      grid-template-rows: auto auto 1fr;
      align-items: center;
      justify-content: center;
      gap: 1.5625vw;
      padding: 7.0313vw;
      background-color: #f3f4f7;
      @media (max-width: vars.$sm) {
        gap: none;
        padding: 20px;
      }

      .title {
        span {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 3.125vw;
          padding: 0 1.4925vw;
          background-color: #000000;
          color: #ffffff;
          border-radius: 1.5625vw;
          font-size: 1.4925vw;
          @media (max-width: vars.$sm) {
            font-size: 18px;
            height: 30px;
            border-radius: 15px;
          }
        }
      }

      .description {
        font-size: 3.9063vw;
        font-weight: 600;
        word-break: keep-all;
        @media (max-width: vars.$sm) {
          font-size: 30px;
        }
        span {
          color: var(--brand-primary);
        }
      }

      .phone-image {
        text-align: center;
        @media (max-width: vars.$sm) {
          display: none;
        }
        img {
          width: 22.8125vw;
        }
      }
    }

    .right {
      display: grid;
      grid-template-rows: 1fr auto;
      gap: 3.9063vw;
      padding: 7.0313vw 6.25vw 1.5625vw;
      @media (max-width: vars.$sm) {
        gap: 20px;
        padding: 20px;
      }
    }
  }
</style>
